import { ComponentType, ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heading, Skeleton } from '@digdir/designsystemet-react';
import classes from './TabbedPageLayout.module.css';

export type PageTab = { key: string; to: string; label: string; icon: ComponentType<{ 'aria-hidden'?: boolean; className?: string }> };
export type PageBreadcrumb = { label: string; to: string };

type TabbedPageLayoutProps = {
  /** Forbokstavene i sirkelen ved tittelen */
  initials: string;
  eyebrow: string;
  title: string;
  isLoading?: boolean;
  tabs: readonly PageTab[];
  tabsLabel: string;
  /** Stien til en underside. Siste ledd er siden du står på. */
  breadcrumbs?: PageBreadcrumb[];
  children: ReactNode;
};

/** Felles topp med navn og faner på tonet bue, og innholdet under */
export const TabbedPageLayout = ({
  initials,
  eyebrow,
  title,
  isLoading = false,
  tabs,
  tabsLabel,
  breadcrumbs,
  children,
}: TabbedPageLayoutProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <div className={classes.page}>
      <div aria-hidden="true" className={classes.backdrop} data-print="false" />
      <div className={classes.content}>
        <header className={classes.header} data-print="false">
          <span aria-hidden="true" className={classes.avatar}>
            {initials}
          </span>
          <div className={classes.heading}>
            <span className={classes.eyebrow}>{eyebrow}</span>
            {isLoading ? (
              <Skeleton variant="text" width={240} />
            ) : (
              <Heading level={1} data-size="xl">
                {title}
              </Heading>
            )}
          </div>
        </header>

        <nav aria-label={tabsLabel} data-print="false">
          <ul className={classes.tabs}>
            {tabs.map(({ key, to, label, icon: Icon }) => (
              <li key={key}>
                <NavLink to={to} className={classes.tab}>
                  <Icon aria-hidden className={classes.tabIcon} />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className={classes.panel}>
          {breadcrumbs && breadcrumbs.length > 0 && (
            <nav aria-label={t('breadcrumbs.ariaLabel')} className="ds-breadcrumbs" data-print="false">
              <ol>
                {breadcrumbs.map((crumb, index) => {
                  const isCurrent = index === breadcrumbs.length - 1;
                  return (
                    <li key={`${crumb.to}-${index}`}>
                      <Link
                        to={isCurrent ? pathname : crumb.to}
                        className="ds-link"
                        aria-current={isCurrent ? 'page' : undefined}
                      >
                        {crumb.label}
                      </Link>
                    </li>
                  );
                })}
              </ol>
            </nav>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};
